import { useMemo } from "react"
import type { MarginLink, SimEngine } from "../order-sim/engine"
import { changeRow, deleteThread, listThreads, postTurn, setMode } from "./api"
import { DRAFTING_NOW, SECTIONS, glance, rerunLine, verdict } from "./decisionModel"
import { useDocsStore } from "./docs.store"
import { DraftPaper, useDraftExport, useDraftSections } from "./DraftPaper"
import { sectionLabel } from "./sections"
import { useSimStore } from "./store"

const CALL_WORD: Record<string, string> = { accepted: "Accepted", partly_accepted: "Partly accepted", rejected: "Rejected" }

/** The Order-in-Appeal simulator on the shared run-through pages (order-sim). */
export const oiaEngine: SimEngine = {
  kind: "oia",
  base: "/oia-sim",
  app: "oia_sim",
  short: "OIA",
  orderName: "Order-in-Appeal",
  untitled: "OIA draft",
  sections: SECTIONS,
  draftingNow: DRAFTING_NOW,
  verdict,
  glance,
  rerunLine,
  callStage: "findings",
  // "Accepted" is always the appellant's ground (in a department appeal, the department's).
  calls: [["accepted", "Accept"], ["partly_accepted", "Accept in part"], ["rejected", "Reject"]],
  callClass: { accepted: "for", partly_accepted: "mixed", rejected: "against" },
  intent: false,
  pauseNext: {
    intake: "Continue to the grounds", grounds: "Continue to the preliminary points", preliminary: "Continue to the findings",
    findings: "Continue to the relief", relief: "Continue to the draft",
  },
  // A ground's finding and the Order say most about a change; name those first.
  rankSection: (id) => (id.startsWith("findings.") ? 0 : id === "order" ? 1 : 2),
  sectionWord: (id) => (id.startsWith("findings.") ? id.slice(9) : id === "order" ? "the Order" : id),
  margin: (sectionId, stages) => {
    const out: MarginLink[] = []
    if (sectionId.startsWith("findings.")) {
      const row = stages.findings?.rows.find((r) => r.id === sectionId.slice(9))
      if (row) out.push({ from: "04", head: `${row.id} ${CALL_WORD[row.call ?? ""] ?? ""}`.trim(), anchor: `nsd-row-findings-${row.id}`, action: "change the call" })
    }
    if (sectionId === "maintainability") {
      const row = stages.preliminary?.rows.find((r) => r.id === "limitation")
      if (row) out.push({ from: "03", head: `${row.label ?? ""} ${row.text}`.trim(), anchor: "nsd-preliminary", action: "change it" })
    }
    if (sectionId === "order") {
      const row = stages.relief?.rows.find((r) => r.id === "outcome")
      if (row) out.push({ from: "05", head: row.text, anchor: "nsd-relief", action: "see the relief" })
    }
    return out
  },
  passedLine: "Passed · figures match 05 · nothing flagged.",

  useThread: (threadId) => useSimStore((s) => s.threads[threadId]),
  hydrate: (threadId, signal) => useSimStore.getState().hydrate(threadId, signal),
  useDocs: (threadId) => {
    const docs = useDocsStore((s) => s.threads[threadId]?.docs)
    return useMemo(
      () => Object.values(docs ?? {}).filter((d) => d.status !== "failed").map((d) => ({ docId: d.docId, filename: d.filename, status: d.status })),
      [docs],
    )
  },
  hydrateDocs: (threadId, signal) => useDocsStore.getState().hydrate(threadId, signal),
  uploadDoc: (threadId, file) => useDocsStore.getState().upload(threadId, file),
  api: { changeRow, deleteThread, listThreads, postTurn, setMode },
  useDraftExport,
  useDraftSections,
  DraftPaper,
  sectionLabel,
  sectionAnchor: (id) => `osia-sec-${id}`,
}
